import cn from "classnames";
import { format } from "date-fns";
import * as Dfns from "date-fns/fp";
import React, { useEffect, useMemo, useState } from "react";
import { MdAdd, MdCancel, MdOutlineTableRows, MdShowChart } from "react-icons/md";
import { BpmDateString } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { RouteComponentProps } from "@reach/router";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  DatePicker,
  DownloadDropdown,
  Dropdown,
  DropdownToggleType,
  IconToggleButton,
  InfoTooltip,
  Spinner
} from "../../Components";
import ChartContainer from "../../Components/ChartContainer";
import WidgetContainer from "../../Components/WidgetContainer";
import { SetError } from "../../redux/modals";
import { DATE_GROUPING_OPTIONS, DateGroupingKey } from "../../TVADCrossChannel/homePageConstants";
import { downloadPNG } from "../../utils/download-utils";
import { convertDateToUTC } from "../../utils/format-utils";
import {
  ForecastChartViewLabels,
  ForecastData,
  ForecastKpiViewOption,
  formatPercentBudget,
  formatRawBudget,
  FullForecastSelection,
  GraphViewOptions,
  TrainTestData
} from "../forecastingConstants";
import {
  getCombinedChannelPlatform,
  getForecastDownloadFilename,
  transformAndDownloadDataToCSV
} from "../forecastingUtils";
import ForecastResultsChart from "./ForecastResultsCharts";
import ForecastResultsTable from "./ForecastResultsTable";

interface ForecastResultsProps {
  selectedForecast: FullForecastSelection;
  budgetSelectableOptions: number[];
  selectNewBudget: (budget: number) => void;
  showWIPFeatures?: boolean;
  chartDateGrouping: DateGroupingKey;
  setChartDateGrouping: (newVal: DateGroupingKey) => void;
  selectedTrainTestData: TrainTestData[];
  selectedForecastedBudgetData: ForecastData<string>[];
  selectedCombinedHistoricalPredictedData: ForecastData<string>[];
  earliestPredictedDate: BpmDateString;
  cid: string;
  setError: SetError;
}

const ForecastResults = ({
  selectedForecast,
  budgetSelectableOptions,
  selectNewBudget,
  showWIPFeatures = false,
  chartDateGrouping,
  setChartDateGrouping,
  selectedTrainTestData,
  selectedForecastedBudgetData,
  selectedCombinedHistoricalPredictedData,
  earliestPredictedDate,
  cid,
  setError,
}: RouteComponentProps & ForecastResultsProps): JSX.Element => {
  const [graphView, setGraphView] = useState<GraphViewOptions>("showGraph");
  const [visibleComparisonBudgets, setVisibleComparisonBudgets] = useState<Set<number>>(new Set());
  const [stagedComparisonBudgets, setStagedComparisonBudgets] = useState<Set<number>>(new Set());
  const [showComparisonBudgetDropdown, setShowComparisonBudgetDropdown] = useState(false);
  const [selectedKpiView, setSelectedKpiView] = useState(ForecastKpiViewOption.TOTAL);
  const [minimumDate, setMinimumDate] = useState<BpmDateString | undefined>();
  const [initialEarliestVisibleDate, setInitialEarliestVisibleDate] = useState<
    BpmDateString | undefined
  >();
  const [earliestVisibleDate, setEarliestVisibleDate] = useState<BpmDateString | undefined>();
  const [latestVisibleDate, setLatestVisibleDate] = useState<BpmDateString | undefined>();
  const [maximumDate, setMaximumDate] = useState<BpmDateString | undefined>();

  const graphDatePickerRange = useMemo(() => {
    return { start: earliestVisibleDate ?? "", end: latestVisibleDate ?? "" };
  }, [earliestVisibleDate, latestVisibleDate]);

  const tableDatePickerRange = useMemo(() => {
    return { start: minimumDate ?? "", end: maximumDate ?? "" };
  }, [maximumDate, minimumDate]);

  // On initial load of the model results, set the visible date range to be 2/3 historical data + 1/3 forecasted data
  useEffect(() => {
    if (
      (earliestVisibleDate && latestVisibleDate) ||
      !selectedTrainTestData ||
      selectedTrainTestData.length === 0 ||
      !selectedForecastedBudgetData ||
      selectedForecastedBudgetData.length === 0
    ) {
      return;
    }

    const lastForecastedDate =
      selectedForecastedBudgetData[selectedForecastedBudgetData.length - 1].date;
    setLatestVisibleDate(lastForecastedDate);
    setMaximumDate(lastForecastedDate);

    const firstTrainedDate = selectedTrainTestData[0].date;
    setMinimumDate(firstTrainedDate);

    const desiredFirstVisibleDateObj = Dfns.subDays(
      selectedForecastedBudgetData.length * 2,
      Dfns.parseISO(lastForecastedDate)
    );
    const desiredFirstVisibleDateStr = format(
      convertDateToUTC(desiredFirstVisibleDateObj),
      "yyyy-MM-dd"
    ) as BpmDateString;
    setInitialEarliestVisibleDate(desiredFirstVisibleDateStr);
    setEarliestVisibleDate(desiredFirstVisibleDateStr);
  }, [earliestVisibleDate, latestVisibleDate, selectedForecastedBudgetData, selectedTrainTestData]);

  const orderedSelectedBudgets = useMemo(
    () => Array.from(visibleComparisonBudgets).sort((a, b) => a - b),
    [visibleComparisonBudgets]
  );

  const allVisibleBudgets = useMemo(() => {
    if (visibleComparisonBudgets.size === 0) {
      return [];
    }

    const visibleComparisonBudgetsArr = Array.from(visibleComparisonBudgets);
    visibleComparisonBudgetsArr.push(selectedForecast.primaryBudget);
    return visibleComparisonBudgetsArr.sort((a, b) => a - b);
  }, [selectedForecast.primaryBudget, visibleComparisonBudgets]);

  const downloadAsCSV = (includeHistorical = false) => {
    transformAndDownloadDataToCSV({
      forecast: selectedForecast,
      data: selectedCombinedHistoricalPredictedData,
      earliestPredictedDate,
      includeHistorical,
      cid,
    });
  };

  const downloadAsPNG = () => {
    const fileName = getForecastDownloadFilename(selectedForecast, cid);
    downloadPNG(".forecastResults", fileName);
  };

  const downloadOptions = [
    {
      label: "CSV: Forecast",
      onClick: () => downloadAsCSV(),
    },
    {
      label: "CSV: Forecast + Historical",
      onClick: () => downloadAsCSV(true),
    },
    {
      label: "PNG",
      onClick: downloadAsPNG,
      disabled: graphView === "showTable",
    },
  ];

  return (
    <WidgetContainer
      collapsible
      header={
        <div className="forecastWidgetHeader">
          <span>How will spend on {selectedForecast.channel} affect </span>
          <Dropdown
            className="forecastKpiViewDropdown"
            onChange={val => setSelectedKpiView(val as ForecastKpiViewOption)}
            options={Object.keys(ForecastChartViewLabels[selectedForecast.outcomeVar]).map(key => ({
              value: key,
              label: ForecastChartViewLabels[selectedForecast.outcomeVar][key],
              selected: selectedKpiView === key,
            }))}
            type={DropdownToggleType.WIDGET_TITLE}
            value={ForecastChartViewLabels[selectedForecast.outcomeVar][selectedKpiView]}
          />
          <span>?</span>
        </div>
      }
      subHeader={
        <div className="forecastBudgetSubHeader">
          <div className="subheaderText">
            Forecasted<InfoTooltip>TODO: Tooltip copy</InfoTooltip>for a budget of{" "}
          </div>
          {budgetSelectableOptions.length > 0 ? (
            <Dropdown
              className="forecastBudgetDropdown"
              size="sm"
              onChange={val => selectNewBudget(Number(val))}
              options={budgetSelectableOptions.map(val => String(val))}
              type={DropdownToggleType.WIDGET_TITLE}
              value={formatRawBudget(selectedForecast.primaryBudget)}
            />
          ) : (
            <Button type={ButtonType.EMPTY} className="forecastingDropdownWithoutOptions">
              {formatRawBudget(selectedForecast.primaryBudget)}
            </Button>
          )}
          {visibleComparisonBudgets.size > 0 && (
            <div
              className={cn("comparisonBudgetTokensContainer", {
                disabled: graphView === "showTable",
              })}
            >
              vs
              {orderedSelectedBudgets.map(budget => (
                <div className="comparisonBudgetToken" key={`comparison-budget-${budget}`}>
                  <span className="comparisonBudgetLabel">{formatRawBudget(budget)}</span>
                  <Button
                    type={ButtonType.EMPTY}
                    variant={ButtonFrameworkVariant.ICON_ONLY}
                    design="secondary"
                    size="sm"
                    className="comparisonBudgetRemoveIcon"
                    icon={<MdCancel />}
                    onClick={() => {
                      visibleComparisonBudgets.delete(budget);
                      setVisibleComparisonBudgets(current => new Set(current));
                    }}
                    disabled={graphView === "showTable"}
                  />
                </div>
              ))}
            </div>
          )}
          <Dropdown
            type={DropdownToggleType.FILLED}
            leadingIcon={<MdAdd />}
            size="sm"
            className="addComparisonBudgetDropdown"
            value="comparison budget"
            options={selectedForecast.comparisonBudgets.map(budget => ({
              value: String(budget),
              selected: stagedComparisonBudgets.has(budget),
              label: formatRawBudget(budget),
              customContent: (
                <div className="dropdownMenuItemLabelRight">
                  {formatPercentBudget(budget / selectedForecast.primaryBudget)}
                </div>
              ),
            }))}
            onToggle={isOpen => {
              setShowComparisonBudgetDropdown(isOpen);
              setStagedComparisonBudgets(new Set(visibleComparisonBudgets));
            }}
            onChange={val => {
              const valNum = Number(val);
              setStagedComparisonBudgets(current => {
                current.has(valNum) ? current.delete(valNum) : current.add(valNum);
                return new Set(current);
              });
            }}
            disabled={graphView === "showTable"}
            applyOnClick={() => {
              setVisibleComparisonBudgets(new Set(stagedComparisonBudgets));
            }}
            setShow={setShowComparisonBudgetDropdown}
            show={showComparisonBudgetDropdown}
            multiSelect
          />
        </div>
      }
      rightActions={
        earliestVisibleDate && latestVisibleDate ? (
          <div className="forecastResultsRightActions">
            {(earliestVisibleDate !== initialEarliestVisibleDate ||
              latestVisibleDate !== maximumDate) &&
              graphView === "showGraph" && (
                <Button
                  type={ButtonType.FILLED}
                  onClick={() => {
                    setEarliestVisibleDate(initialEarliestVisibleDate);
                    setLatestVisibleDate(maximumDate);
                  }}
                >
                  Reset Date Range
                </Button>
              )}
            <DatePicker
              range={graphView === "showGraph" ? graphDatePickerRange : tableDatePickerRange}
              onChange={({ start, end }) => {
                setEarliestVisibleDate(start as BpmDateString);
                setLatestVisibleDate(end as BpmDateString);
              }}
              isOutsideRange={date => {
                if (!minimumDate || !maximumDate) {
                  return true;
                }
                return date < minimumDate || date > maximumDate;
              }}
              disabled={graphView === "showTable"}
            />
          </div>
        ) : undefined
      }
    >
      {selectedCombinedHistoricalPredictedData && earliestVisibleDate && latestVisibleDate ? (
        <div className="forecastResults">
          <ChartContainer
            enableHoverDesign
            rightActions={
              <div className="forecastResultsRightActions">
                <IconToggleButton
                  options={[
                    { key: "showTable", icon: <MdOutlineTableRows />, label: "table view" },
                    { key: "showGraph", icon: <MdShowChart />, label: "graph view" },
                  ]}
                  size="sm"
                  selectedOption={graphView}
                  onChange={() =>
                    setGraphView(prev => (prev === "showGraph" ? "showTable" : "showGraph"))
                  }
                />
                <DownloadDropdown
                  size="sm"
                  menuOptions={downloadOptions.map(option => option.label)}
                  onClickOptions={downloadOptions.map(option => option.onClick)}
                  disabledMenuOptions={downloadOptions.reduce((acc, option) => {
                    acc[option.label] = option.disabled ?? false;
                    return acc;
                  }, {})}
                />
              </div>
            }
            title={
              <>
                {showWIPFeatures && (
                  <Dropdown
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => {
                      // [DEV-18381] TODO: Support date group
                      setChartDateGrouping(option);
                      setError({
                        message: "Updating date grouping of forecasting charts not yet supported",
                      });
                    }}
                  />
                )}
                <div>
                  {getCombinedChannelPlatform(selectedForecast.channel, selectedForecast.platform)}{" "}
                  Spend and {ForecastChartViewLabels[selectedForecast.outcomeVar][selectedKpiView]}
                </div>
                <div className="headerText">(Historical and Forecasted)</div>
              </>
            }
          >
            {graphView === "showGraph" ? (
              <ForecastResultsChart
                data={selectedCombinedHistoricalPredictedData}
                earliestVisibleDate={earliestVisibleDate}
                setEarliestVisibleDate={setEarliestVisibleDate}
                latestVisibleDate={latestVisibleDate}
                setLatestVisibleDate={setLatestVisibleDate}
                earliestPredictedDate={earliestPredictedDate}
                dateGrouping={chartDateGrouping}
                channel={selectedForecast.channel}
                outcomeVar={selectedForecast.outcomeVar}
                selectedKpiView={selectedKpiView}
                allComparisonBudgets={selectedForecast.comparisonBudgets}
                selectedComparisonBudgets={allVisibleBudgets}
                syncId={2}
                primaryBudget={selectedForecast.primaryBudget}
              />
            ) : (
              <ForecastResultsTable
                data={selectedForecastedBudgetData}
                forecast={selectedForecast}
              />
            )}
          </ChartContainer>
        </div>
      ) : (
        <div className="forecastingWidgetLoader forecastResultsLoader">
          <Spinner size={50} />
        </div>
      )}
    </WidgetContainer>
  );
};

export default ForecastResults;
